import { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { DayPicker, DateRange } from 'react-day-picker';
import { cn } from '@/utils';
import { useLanguage } from '@/context/LanguageContext';
import 'react-day-picker/dist/style.css';

interface DateRangePickerProps {
  from: string | null;
  to: string | null;
  onChange: (from: string | null, to: string | null) => void;
  className?: string;
  placeholder?: string;
}

export function DateRangePicker({
  from,
  to,
  onChange,
  className,
  placeholder = 'Select date range',
}: DateRangePickerProps) {
  const { language, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize selected range when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setSelectedRange({
        from: from ? new Date(from) : undefined,
        to: to ? new Date(to) : undefined,
      });
    }
  }, [isOpen, from, to]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format date for display
  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return format(date, 'PP', { locale: language === 'ar' ? ar : undefined });
  };

  const handleDaySelect = (range: DateRange | undefined) => {
    setSelectedRange(range);
  };

  const handleApply = () => {
    if (selectedRange?.from) {
      const fromDate = selectedRange.from.toISOString();
      const toDate = selectedRange.to ? selectedRange.to.toISOString() : selectedRange.from.toISOString();
      onChange(fromDate, toDate);
    } else {
      onChange(null, null);
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    setSelectedRange({ from: undefined, to: undefined });
    onChange(null, null);
    setIsOpen(false);
  };

  const handleQuickSelect = (days: number) => {
    const today = new Date();
    const fromDate = new Date(today);
    fromDate.setDate(today.getDate() - days);
    
    setSelectedRange({ from: fromDate, to: today });
  };

  const displayText = () => {
    if (from && to) {
      return `${formatDate(from)} - ${formatDate(to)}`;
    }
    if (from) {
      return `${t('dateRange.from')} ${formatDate(from)}`;
    }
    if (to) {
      return `${t('dateRange.until')} ${formatDate(to)}`;
    }
    return placeholder;
  };

  const hasValue = from || to;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-colors w-full justify-between min-w-[200px]',
          'bg-background hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          hasValue ? 'border-primary/50' : 'border-input'
        )}
      >
        <CalendarIcon className={cn('h-4 w-4 flex-shrink-0', hasValue ? 'text-primary' : 'text-muted-foreground')} />
        <span className={cn('truncate flex-1 text-left', hasValue ? 'text-foreground' : 'text-muted-foreground')}>
          {displayText()}
        </span>
        {hasValue && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            className="p-0.5 hover:bg-muted rounded-md transition-colors flex-shrink-0"
          >
            <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
          </button>
        )}
      </button>

      {isOpen && (
        <div className="absolute z-50 top-full mt-2 p-4 bg-background border rounded-lg shadow-lg animate-fade-in">
          {/* Quick select buttons */}
          <div className="flex gap-1 mb-3 flex-wrap">
            <button
              type="button"
              onClick={() => handleQuickSelect(7)}
              className="px-2 py-1 text-xs font-medium bg-muted hover:bg-muted/80 rounded-md transition-colors"
            >
              {t('dateRange.last7Days')}
            </button>
            <button
              type="button"
              onClick={() => handleQuickSelect(30)}
              className="px-2 py-1 text-xs font-medium bg-muted hover:bg-muted/80 rounded-md transition-colors"
            >
              {t('dateRange.last30Days')}
            </button>
            <button
              type="button"
              onClick={() => handleQuickSelect(90)}
              className="px-2 py-1 text-xs font-medium bg-muted hover:bg-muted/80 rounded-md transition-colors"
            >
              {t('dateRange.last90Days')}
            </button>
          </div>

          {/* Calendar */}
          <DayPicker
            mode="range"
            selected={selectedRange}
            onSelect={handleDaySelect}
            numberOfMonths={1}
            pagedNavigation
            showOutsideDays={false}
            locale={language === 'ar' ? ar : undefined}
            classNames={{
              months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
              month: 'space-y-4',
              caption: 'flex justify-center pt-1 relative items-center',
              caption_label: 'text-sm font-medium',
              nav: 'space-x-1 flex items-center',
              nav_button: cn(
                'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 border border-input rounded-md flex items-center justify-center'
              ),
              nav_button_previous: 'absolute left-1',
              nav_button_next: 'absolute right-1',
              table: 'w-full border-collapse space-y-1',
              head_row: 'flex',
              head_cell: 'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',
              row: 'flex w-full mt-2',
              cell: 'text-center text-sm p-0 relative',
              day: cn(
                'h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-muted rounded-md transition-colors'
              ),
              day_selected:
                'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
              day_today: 'bg-accent text-accent-foreground',
              day_outside: 'text-muted-foreground opacity-50',
              day_disabled: 'text-muted-foreground opacity-50',
              day_range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
              day_hidden: 'invisible',
            }}
          />

          {/* Action buttons */}
          <div className="flex gap-2 mt-4 pt-3 border-t">
            <button
              type="button"
              onClick={handleClear}
              className="flex-1 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
            >
              {t('common.clear')}
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="flex-1 px-3 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors"
            >
              {t('filter.apply')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
